import { noop, set } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { H2, H3 } from '@ad-scripts/presentation/components/common/headers';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { Body, Row } from 'src/presentation/components/forms/Layout';
import { ExternalSource } from 'src/actions/integrations/integrationSources';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import lookupDebuggerDetailedResource from 'src/actions/integrations/lookupAdapterDebugging/lookupDebuggerDetailedResource';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import { WrappedField } from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';

interface SearchFormValues {
  appId: string;
  searchEmail?: string;
  searchPhone?: string;
  queryAttr: {
    [k: string]: string | undefined;
  };
}

export enum DebugFlowType {
  BASIC = 'basic',
  AUTOLINKING = 'autolinking',
}

type SearchPanelProps = {
  enabledIntegrations: ExternalSource[];
  loadingState: string;
  onSubmit: Function;
  workFlowTitle: string;
  unloadResources: Function;
  flowType: DebugFlowType;
};

export const SearchPanel: FC<SearchPanelProps> = ({
  unloadResources,
  enabledIntegrations,
  loadingState,
  onSubmit,
  workFlowTitle,
  flowType,
}) => {
  const dispatch = useDispatch();
  const [appDropdownOptions, setAppDropdownOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [searchQueryAttr, setSearchQueryAttr] = useState<Array<{ attr: string; label: string }>>();
  const [enabled, setEnabled] = useState<boolean>(false);
  // unused state required to trigger re-render on dropdown change
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [_, setRerender] = useState({});
  const enabledRef = useRef<boolean>(false);

  const getDropdownOptions = (integrations: ExternalSource[]) => {
    const options = integrations.map(i => {
      return {
        label: i.name,
        value: i.id,
      };
    });
    setAppDropdownOptions(options);
  };

  const formContext = useForm<SearchFormValues>({
    item: {
      appId: '',
      searchEmail: '',
      searchPhone: '',
      queryAttr: {},
    },
    resetErrors: () => noop,
    onSave: () => noop,
  });

  useEffect(() => {
    getDropdownOptions(enabledIntegrations);
  }, [enabledIntegrations]);

  const getSearchAttributes = integrationApp => {
    // reset queryAttr when getting new ones
    set(formContext.workingStateRef.current, 'queryAttr', {});
    // only get search query attributes if you're debugging the basic flow
    if (flowType === DebugFlowType.BASIC) {
      const searchAttributes = integrationApp?.customerLookupSettings?.searchQueryAttributes;
      searchAttributes && setSearchQueryAttr(searchAttributes);
    }
  };

  const checkDisabled = () => {
    const appId = formContext.workingStateRef.current.appId.trim();
    const email = (formContext.workingStateRef.current.searchEmail || '').trim();
    const phone = (formContext.workingStateRef.current.searchPhone || '').trim();
    const hasEmailPhone = appId && (email || phone);
    const hasAttrs = checkRequiredSearchAttr(formContext.workingStateRef.current.queryAttr);

    const enableTests = !!(hasEmailPhone && appId && hasAttrs);
    if (enableTests !== enabledRef.current) {
      enabledRef.current = enableTests;
      setEnabled(enableTests);
    }
  };

  const handleSelectChange = e => {
    if (formContext.workingStateRef.current.appId === e) {
      return;
    }
    if (enabledIntegrations && enabledIntegrations.length > 0) {
      const selectedAppData = enabledIntegrations.find(({ id }) => id === e);
      if (selectedAppData) {
        formContext.workingStateRef.current.appId = selectedAppData.id;
      }
      getSearchAttributes(selectedAppData);
    }
    checkDisabled();
    unloadResources();
    setRerender({});
  };

  const handleCustomerLookupFieldChange = (value: string, attr: string) => {
    set(formContext.workingStateRef.current, attr, value);
    checkDisabled();
  };

  const handleCustomQueryAttrChange = (value: string, attr: string) => {
    set(formContext.workingStateRef.current.queryAttr, attr, value);
    checkDisabled();
  };

  const checkRequiredSearchAttr = queryAttr => {
    if (flowType === DebugFlowType.AUTOLINKING) {
      return true;
    }

    let queryAttrCount = 0;
    for (const key in queryAttr) {
      if ((queryAttr[key] || '').trim()) {
        queryAttrCount++;
      }
    }

    return queryAttrCount > 0;
  };

  const handleSubmit = () => {
    const { appId, searchEmail, searchPhone, queryAttr } = formContext.workingStateRef.current;
    const formValues = { appId: appId, queryAttr: queryAttr };
    formValues.queryAttr['searchEmail'] = searchEmail;
    formValues.queryAttr['searchPhone'] = searchPhone;
    onSubmit(formValues);
    dispatch(lookupDebuggerDetailedResource.effects.unload());
  };

  return (
    <ScrollContainer>
      <SearchPanelContainer data-aid="debugging-search-panel">
        {loadingState === 'unstarted' || loadingState === 'loading' ? (
          <Spinner />
        ) : (
          <FormBody data-aid="debug-basic-search">
            <H2 data-aid="search-panel-title">{workFlowTitle}</H2>
            {enabledIntegrations?.length === 0 ? (
              <PBold>
                There are no apps available to debug, check that the app you wish to debug is enabled in the Apps
                configuration page.
              </PBold>
            ) : (
              <>
                <FormLayout data-aid="form-fields">
                  <Form context={formContext}>
                    <SearchSection>
                      <StyledH3>Step 1 - Search for Customer in Gladly</StyledH3>
                      <SearchPanelRow>
                        <PStyled>
                          Enter an email address or a phone number to find a customer profile in Gladly to test.
                        </PStyled>
                      </SearchPanelRow>
                      <SearchPanelRow>
                        <WrappedField
                          data-aid="search-email-input"
                          attr="searchEmail"
                          label="Email"
                          placeholder="Email"
                          onChange={value => handleCustomerLookupFieldChange(value, 'searchEmail')}
                        />
                      </SearchPanelRow>
                      <SearchPanelRow>
                        <WrappedField
                          data-aid="phone-input"
                          attr="searchPhone"
                          label="Phone"
                          placeholder="Phone"
                          description="Phone number must include country code"
                          onChange={value => handleCustomerLookupFieldChange(value, 'searchPhone')}
                        />
                      </SearchPanelRow>
                    </SearchSection>
                    <StyledDivider />
                    <SearchSection>
                      <StyledH3>Step 2 - Debug Lookup Adapter</StyledH3>
                      <SearchPanelRow>
                        <PStyled>
                          Select a lookup adapter and enter one or more search query attributes to test.
                        </PStyled>
                      </SearchPanelRow>
                      <SearchPanelRow>
                        <Dropdown
                          data-aid="search-panel-app-select"
                          attr="appSelect"
                          options={appDropdownOptions}
                          placeholder="Select an enabled app"
                          label="Lookup Adapter"
                          onChange={handleSelectChange}
                          value={formContext.workingStateRef.current.appId}
                        />
                      </SearchPanelRow>
                      {searchQueryAttr &&
                        searchQueryAttr.map(queryAttr => {
                          const { attr, label } = queryAttr;
                          return (
                            <SearchPanelRow key={attr}>
                              <WrappedField
                                data-aid={`${attr}`}
                                attr={attr}
                                label={label}
                                placeholder={label}
                                onChange={value => handleCustomQueryAttrChange(value, attr)}
                              />
                            </SearchPanelRow>
                          );
                        })}
                    </SearchSection>
                  </Form>
                </FormLayout>

                <ButtonSubmitRow>
                  <Button data-aid="search-panel-submit" onClick={handleSubmit} disabled={!enabled}>
                    Test
                  </Button>
                </ButtonSubmitRow>
              </>
            )}
          </FormBody>
        )}
      </SearchPanelContainer>
    </ScrollContainer>
  );
};

const ScrollContainer = styled.div`
  margin-top: 100px;
`;

const SearchPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 378px;
  overflow-y: auto;
  width: 378px;
  background-color: ${p => p.theme.colors.white};
  height: 100%;
`;

const PBold = styled.p`
  font-size: ${p => p.theme.fontSize.base};
  font-weight: ${p => p.theme.fontWeight.heavy};
  margin-bottom: 4px;
`;

const FormBody = styled(Body)`
  width: 100%;
`;

const SearchPanelRow = styled(Row)`
  margin-bottom: 12px;
`;

const ButtonSubmitRow = styled(Row)`
  margin-top: 4px;
`;

const SearchSection = styled.div`
  margin: 8px 0 16px 0;
`;

const PStyled = styled.p`
  margin: 4px;
`;

const FormLayout = styled.div`
  margin: 8px 0;
`;

const StyledDivider = styled.hr`
  border-top: 1px solid ${p => p.theme.colors.gray300};
  width: 256px;
  margin-left: 0;
  margin-bottom: 20px;
`;

const StyledH3 = styled(H3)`
  margin-bottom: 8px;
`;
